import React, { Component } from 'react';
import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import { injectStateService } from '@/shared/services/StateService';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from '../hrmMessages';
import Axios from 'axios';
import EmployeeRelationMatrixFilters, {
  defaultFilters as defaultRelationMatrixFilters,
} from '@/modules/hrm/assignmentAnalysis/EmployeeRelationMatrixFilters';
import Button from '@/shared/components/Button';
import { FaCog, FaFileExcel } from 'react-icons/fa';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import PropTypes from 'prop-types';
import NavigateToState from '@/shared/components/NavigateToState';
import { functionalities } from '@/shared/services/AuthService';
import userStorage from '@/shared/services/userStorage';
import CapacityReportFilters, {
  defaultFilters as defaultCapacityFilters,
} from '@/modules/hrm/assignmentAnalysis/CapacityReportFilters';
import { emptyGuid, guid1 } from '@/shared/utils/guid';
import SignalRService from '@/shared/services/SignalRService';

const getSessionStorageKey = matrixType => {
  switch (matrixType) {
    case 'relation':
      return 'AssignmentAnalysisRelationMatrix';
    case 'task':
      return 'AssignmentAnalysisTaskMatrix';
    case 'capacity':
      return 'AssignmentAnalysisCapacityReport';
  }
};

const getColumnSelectionStorageKey = matrixType => {
  switch (matrixType) {
    case 'relation':
      return 'RelationMatrixHiddenColumns';
    case 'task':
      return 'TaskMatrixHiddenColumns';
    case 'capacity':
      return 'RelationCapacityMatrixHiddenColumns';
  }
};

const getZendeskArticle = matrixType => {
  switch (matrixType) {
    case 'relation':
      return '21392672273052';
    case 'task':
      return '21392660992924';
    case 'capacity':
      return '24671462481692';
  }
};

class AssignmentAnalysisMatrix extends Component {
  state = {
    items: [],
    filters: {},
    exporting: false,
  };

  constructor(props) {
    super(props);
    const { matrixType } = this.props;
    const sessionStorageKey = getSessionStorageKey(matrixType);
    const columnSelectionStorageKey = getColumnSelectionStorageKey(matrixType);
    const sessionParams = JSON.parse(sessionStorage.getItem(sessionStorageKey)) || {};

    const defaultFilters =
      matrixType === 'relation' ? defaultRelationMatrixFilters : defaultCapacityFilters;

    this.state = {
      sessionStorageKey,
      columnSelectionStorageKey,
      filters: {
        ...defaultFilters,
        ...sessionParams,
      },
      initialSearchParams: {
        ...defaultFilters,
        ...sessionParams,
      },
      items: [],
      parametersForLastApiCall: null,
      columns: [],
      hideableColumns: [],
      showColumnSelectionModal: false,
    };
  }

  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  applySorting(rows, searchParams) {
    const sign = searchParams.desc ? -1 : 1;
    if (searchParams.sortKey === 'employee') {
      rows = rows.sort((a, b) => sign * a.employee.localeCompare(b.employee));
    } else if (searchParams.sortKey === 'employeeRole') {
      rows = rows.sort(
        (a, b) =>
          sign * a.employeeRole.localeCompare(b.employeeRole) ||
          a.employee.localeCompare(b.employee),
      );
    } else if (searchParams.sortKey === 'total') {
      rows = rows.sort(
        (a, b) => sign * (a.total - b.total) || a.employee.localeCompare(b.employee),
      );
    } else if (searchParams.sortKey !== '') {
      const column = this.state.hideableColumns.findIndex(col => col.id === searchParams.sortKey);
      if (column >= 0) {
        rows = rows.sort(
          (a, b) =>
            sign * (a.counts[column] - b.counts[column]) || a.employee.localeCompare(b.employee),
        );
      }
    }
    return rows;
  }

  columnsFromHideableColumns = hideableColumns =>
    hideableColumns.map((c, i) => (c.selected ? i : -1)).filter(c => c >= 0);

  loadContinuation = (serializedParameters, data, users) => {
    if (!data) return [];
    const newRows = data.rows;
    const { columnSelectionStorageKey } = this.state;

    const storageState = userStorage.getItem(columnSelectionStorageKey) || {};
    const hideableColumns = data.columns.map(c => ({
      id: c.id,
      label: c.label,
      selected: c.id in storageState ? storageState[c.id] : true,
    }));

    const columns = this.columnsFromHideableColumns(hideableColumns);

    const userMap = {};
    for (const user of users || []) {
      userMap[user.id] = user;
    }

    for (const row of newRows) {
      if (!row.employee || !row.employeeRole) {
        const user = userMap[row.id] || {};
        row.employee = user.employee || '';
        row.employeeRole = user.employeeRole || '';
        row.employeeWeeklyHours = user.employeeWeeklyMinutes || 0;
        row.employeeAvailableHours = user.employeeAvailableMinutes || 0;
      }
    }

    this.setState(state => ({
      ...state,
      items: newRows,
      parametersForLastApiCall: serializedParameters,
      hideableColumns,
      columns,
    }));
    return newRows;
  };

  errorContinuation = error => {
    const { intl } = this.props;
    if (!Axios.isCancel(error)) return handleHttpError(intl)(error);
  };

  createExcelReport = () => {
    const { filters } = this.state;
    const params = {
      ...filters,
      culture: this.props.intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ exporting: true });

    return api.base
      .post(`${config.appUrls.tasks}/api/CapacityPlanning/EmployeeReportDataExport`, params, {
        params,
      })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };

  loadItems = searchParams => {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();

    const { filters, sessionStorageKey } = this.state;
    const params = { ...filters, languageCode: this.props.intl.locale };
    const serializedParameters = JSON.stringify(params);
    if (this.state.parametersForLastApiCall === serializedParameters) {
      return this.applySorting(this.state.items, searchParams);
    }

    const sessionParams = JSON.stringify(filters);
    sessionStorage.setItem(sessionStorageKey, sessionParams);

    let apiCall = undefined;
    if (this.props.matrixType === 'relation') {
      apiCall = api.base.post(
        `${config.appUrls.api}/api/AssignmentAnalysis/EmployeeRelation`,
        params,
        {
          cancelToken: this.reportToken.token,
        },
      );
    } else if (this.props.matrixType === 'task') {
      apiCall = api.base.post(
        `${config.appUrls.tasks}/api/AssignmentAnalysis/EmployeeTask`,
        params,
        {
          cancelToken: this.reportToken.token,
        },
      );
    } else if (this.props.matrixType === 'capacity') {
      apiCall = api.base.post(
        `${config.appUrls.tasks}/api/CapacityPlanning/EmployeeReport`,
        params,
        {
          cancelToken: this.reportToken.token,
        },
      );
    }

    let usersCall = api.base.post(
      `${config.appUrls.api}/api/AssignmentAnalysis/Employees`,
      params,
      {
        cancelToken: this.reportToken.token,
      },
    );

    return Promise.all([
      apiCall.catch(error => this.errorContinuation(error)),
      usersCall.catch(error => this.errorContinuation(error)),
    ]).then(
      ([response, usersResponse]) =>
        response &&
        usersResponse &&
        this.applySorting(
          this.loadContinuation(serializedParameters, response.data, usersResponse.data),
          searchParams,
        ),
    );
  };

  filtersChanged(filters) {
    let newFilters = {
      ...(this.state.filters || {}),
      ...filters,
    };

    this.setState(state => ({
      filters: newFilters,
      initialSearchParams: {
        ...filters,
      },
      items: [],
    }));
  }

  handleColumnSelection(hideableColumns) {
    const { columnSelectionStorageKey } = this.state;
    const storageState = hideableColumns.reduce(
      (acc, col) => ({ ...acc, [col.id]: col.selected }),
      {},
    );
    userStorage.setItem(columnSelectionStorageKey, storageState);

    const columns = this.columnsFromHideableColumns(hideableColumns);
    this.setState(state => ({
      ...state,
      showColumnSelectionModal: false,
      hideableColumns,
      columns,
    }));
  }

  renderRow = row => {
    const { columns } = this.state;
    const { intl, matrixType } = this.props;

    // This array is setup to match the backend Enum values of "EmployeeProfile"
    // Index values match those of the enum values...
    const fieldMap = [
      undefined,
      'responsible',
      'assistant',
      'assistantBackup',
      'associate',
      'billingUser',
      'seniorUser',
    ];

    const hours = intl.formatMessage(messages.HoursAbbreviated);
    const renderValue = x => {
      if (matrixType !== 'capacity') return x;
      const value = Math.round(x / 60);
      return value === 0 ? '-' : value + hours;
    };

    const renderCellContent = (row, column) => {
      const { hideableColumns, filters } = this.state;
      const { matrixType } = this.props;
      const value = renderValue(row.counts[column]);

      let stateName = '';
      let stateParams = {};
      const columnDefinition = hideableColumns[column];
      const columnId = columnDefinition.id;

      if (matrixType === 'relation') {
        const field = fieldMap[parseInt(columnId)];
        if (!!field) {
          stateName = 'relations.overview';
          stateParams = { [field]: row.id };
          if (filters.employeeId) {
            if (!!filters.employeeProfile) {
              stateParams[fieldMap[filters.employeeProfile]] = filters.employeeId;
            } else {
              stateParams['assignedTo'] = filters.employeeId;
            }
          }
        }
      }
      if (!stateName) return <span>{value}</span>;
      return (
        <NavigateToState
          stateName={stateName}
          useUrl
          paramsToJson
          title={intl.formatMessage(
            matrixType === 'relation'
              ? messages.ShowInRelationsOverview
              : messages.ShowInTasksOverview,
          )}
          functionality={functionalities.Relation}
          permission="Read"
          stateParams={stateParams}
        >
          {value}
        </NavigateToState>
      );
    };

    const header = !!row.employee
      ? row.employee
      : row.id !== emptyGuid
      ? intl.formatMessage(messages.Totals)
      : intl.formatMessage(messages.Unassigned);

    return (
      <tbody className="hover" key={'row_' + row.id}>
        <tr>
          <td>{header}</td>
          <td>{row.employeeRole}</td>
          {matrixType === 'capacity' && <td>{renderValue(row.employeeWeeklyHours)}</td>}
          {matrixType === 'capacity' && <td>{renderValue(row.employeeAvailableHours)}</td>}
          {columns.map(column => (
            <td key={row.id + '_' + column}>{renderCellContent(row, column)}</td>
          ))}
          <td>{renderValue(row.total)}</td>
        </tr>
      </tbody>
    );
  };

  render() {
    const { intl, matrixType } = this.props;
    const {
      initialSearchParams,
      filters,
      items,
      showColumnSelectionModal,
      columns,
      hideableColumns,
      exporting,
    } = this.state;

    let title = '';
    switch (matrixType) {
      case 'relation':
        title = intl.formatMessage(messages.AssignmentAnalysisEmployeeRelationMatrix);
        break;
      case 'task':
        title = intl.formatMessage(messages.AssignmentAnalysisEmployeeTaskMatrix);
        break;
      case 'capacity':
        title = intl.formatMessage(messages.EmployeeCapacityReport);
        break;
    }

    const zendeskArticle = getZendeskArticle(matrixType);
    const showRelationFilters = matrixType === 'relation';
    const showCapacityFilters = matrixType === 'capacity';

    console.log('filters', filters);

    return (
      <div className="relation-matrix">
        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={c => this.handleColumnSelection(c)}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}
        <SimplePanel article={zendeskArticle} title={title}>
          {showRelationFilters && (
            <div className="info-block container-fluid">
              <EmployeeRelationMatrixFilters
                filters={filters}
                columns={hideableColumns || []}
                onFilterChange={filters => this.filtersChanged(filters)}
              />
            </div>
          )}
          {showCapacityFilters && (
            <div className="info-block container-fluid">
              <CapacityReportFilters
                filters={filters}
                columns={hideableColumns || []}
                onFilterChange={filters => this.filtersChanged(filters)}
              />
            </div>
          )}
        </SimplePanel>

        <div className="row btn-row">
          <div className="col-md-6 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.setState({ showColumnSelectionModal: true })}
            >
              <span className="margin5R">
                <FaCog className="react-icon" />
              </span>
              <FormattedMessage {...messages.ColumnConfig} />
            </Button>
            {showCapacityFilters && (
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.createExcelReport()}
                disabled={exporting}
              >
                <span className="margin5R">
                  <FaFileExcel className="file-icon" />
                </span>
                {intl.formatMessage(messages.ExportToExcel)}
              </Button>
            )}
          </div>
        </div>

        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item)}
          noDataLabel={<FormattedMessage {...messages.NoEmployeesFound} />}
        >
          <ColumnHeader
            title={intl.formatMessage(messages.Employee)}
            sortKey={'employee'}
            style={{ width: '200px', tableLayout: 'fixed' }}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.EmployeeRole)}
            sortKey={'employeeRole'}
            style={{ width: '200px', tableLayout: 'fixed' }}
          />
          {matrixType === 'capacity' && (
            <ColumnHeader
              title={intl.formatMessage(messages.EmployeeWeeklyCapacity)}
              sortKey={'employeeWeeklyCapacity'}
              style={{ width: '200px', tableLayout: 'fixed' }}
            />
          )}
          {matrixType === 'capacity' && (
            <ColumnHeader
              title={intl.formatMessage(messages.EmployeeTotalCapacity)}
              sortKey={'employeeTotalCapacity'}
              style={{ width: '200px', tableLayout: 'fixed' }}
            />
          )}

          {hideableColumns &&
            hideableColumns.map((col, index) => (
              <ColumnHeader
                key={col.id}
                title={col.label}
                sortKey={col.id}
                visible={columns.includes(index)}
                style={{ width: '200px', tableLayout: 'fixed' }}
              />
            ))}

          <ColumnHeader title={intl.formatMessage(messages.Totals)} sortKey={'total'} />
        </ScrollableTable>
      </div>
    );
  }
}

AssignmentAnalysisMatrix.propTypes = {
  intl: intlShape.isRequired,
  matrixType: PropTypes.string,
};

AssignmentAnalysisMatrix.defaultProps = {
  matrixType: 'unknown',
};

export default injectStateService(injectIntl(AssignmentAnalysisMatrix));
